/**
 * Session + lock state. Holds the in-memory UnlockSession (the UMK) while
 * unlocked; wipes it on lock, idle-timeout, or tab close. The UMK lives ONLY
 * here — never in localStorage/sessionStorage/IndexedDB (portal-spec §1/§2).
 */
import { create } from 'zustand';
import type { Me } from '@heivol-locker/shared-types';
import type { UnlockSession, VaultClient } from '@heivol-locker/vault-client';
import { ApiError } from '@heivol-locker/vault-client';
import { createClient } from '../lib/client';
import {
  buildEnrolment,
  recoverWithCode,
  unlockSession,
  wipeSession,
} from '@heivol-locker/vault-crypto';

export type Phase = 'loading' | 'login' | 'welcome' | 'unlock' | 'recovery' | 'unlocked' | 'error';

/** Default idle auto-lock window (portal-spec §2: 10 min, configurable 1–60). */
const IDLE_MS = 10 * 60 * 1000;

interface SessionState {
  phase: Phase;
  me: Me | null;
  client: VaultClient;
  usingMock: boolean;
  session: UnlockSession | null;
  /** Set once immediately after enrolment so the UI can show it exactly once. */
  recoveryCode: string | null;
  busy: boolean;
  error: string | null;
  init: () => Promise<void>;
  enrol: (passphrase: string) => Promise<void>;
  unlock: (passphrase: string) => Promise<void>;
  beginRecovery: () => void;
  cancelRecovery: () => void;
  recover: (recoveryCode: string, newPassphrase: string) => Promise<void>;
  lock: () => void;
  logout: () => void;
  acknowledgeRecovery: () => Promise<void>;
  /** Begin a vault wipe (lost passphrase AND recovery code). The server emails a signed link to
   *  the account's email; the response carries NO token (the email is the out-of-band factor). */
  wipeStart: () => Promise<{ ok: true; expiresAt: string }>;
  /** Token-free all-device cancel: cancels a pending wipe, or restores a wiped vault inside the
   *  72h restore window. Usable from any signed-in session. */
  wipeCancel: () => Promise<void>;
  /** Confirm the wipe from the /wipe page. Throws on an invalid/expired token. */
  wipeConfirm: (token: string) => Promise<void>;
  dismissError: () => void;
}

const handle = createClient();

let lockTimer: ReturnType<typeof setTimeout> | undefined;

function errorMessage(e: unknown): string {
  if (e instanceof ApiError) {
    return e.message;
  }
  return e instanceof Error ? e.message : String(e);
}

export const useSession = create<SessionState>((set, get) => ({
  phase: 'loading',
  me: null,
  client: handle.client,
  usingMock: handle.usingMock,
  session: null,
  recoveryCode: null,
  busy: false,
  error: null,

  init: async () => {
    // Real API + no stored heivol-id JWT → show the login gate; don't call me()
    // (it would 401). The mock path has no login and auto-uses the dev identity.
    if (!get().usingMock && localStorage.getItem('hv-jwt') === null) {
      set({ phase: 'login' });
      return;
    }
    try {
      const me = await get().client.me();
      set({ me, phase: me.enrolled ? 'unlock' : 'welcome' });
    } catch (e) {
      set({ phase: 'error', error: errorMessage(e) });
    }
  },

  enrol: async (passphrase: string) => {
    set({ busy: true, error: null });
    try {
      const { request, session, recoveryCode } = await buildEnrolment(passphrase);
      const res = await get().client.enroll(request);
      get().client.setSession(session);
      set({ me: res.me, session, recoveryCode, phase: 'unlocked', busy: false });
      armAutoLock();
    } catch (e) {
      set({ error: errorMessage(e), busy: false });
    }
  },

  unlock: async (passphrase: string) => {
    set({ busy: true, error: null });
    const client = get().client;
    try {
      const env = await client.getUnlockEnvelopes();
      const session = await unlockSession(passphrase, env);
      client.setSession(session);
      await client.recordUnlockAttempt({ outcome: 'success' });
      set({ session, phase: 'unlocked', busy: false });
      armAutoLock();
    } catch {
      // Generic — no oracle about passphrase closeness (portal-spec §6).
      try {
        await client.recordUnlockAttempt({ outcome: 'failure' });
      } catch {
        /* best-effort accounting */
      }
      set({ error: 'That passphrase did not unlock your vault.', busy: false });
    }
  },

  beginRecovery: () => set({ phase: 'recovery', error: null }),

  cancelRecovery: () => set({ phase: 'unlock', error: null }),

  recover: async (recoveryCode: string, newPassphrase: string) => {
    set({ busy: true, error: null });
    const client = get().client;
    try {
      // start returns the recovery envelope + salt/params (no email round-trip in
      // this build — the signed-link + 72h cooldown flow is a later server step).
      const start = await client.recoveryStart();
      const { request, session } = await recoverWithCode(recoveryCode, newPassphrase, start);
      const res = await client.recoveryComplete(request);
      client.setSession(session);
      set({ me: res.me, session, phase: 'unlocked', busy: false });
      armAutoLock();
    } catch {
      // Generic — no oracle about recovery-code validity (portal-spec §6).
      set({
        error: 'That recovery code did not match. Check the 24 words and try again.',
        busy: false,
      });
    }
  },

  lock: () => {
    const { session, client } = get();
    if (session !== null) {
      wipeSession(session);
    }
    client.setSession(null);
    if (lockTimer !== undefined) {
      clearTimeout(lockTimer);
      lockTimer = undefined;
    }
    set({ session: null, recoveryCode: null, phase: 'unlock' });
  },

  logout: () => {
    const { session, client } = get();
    if (session !== null) {
      wipeSession(session);
    }
    client.setSession(null);
    if (lockTimer !== undefined) {
      clearTimeout(lockTimer);
      lockTimer = undefined;
    }
    try {
      localStorage.removeItem('hv-jwt');
      localStorage.removeItem('hv-refresh');
    } catch {
      /* storage may be unavailable */
    }
    set({ session: null, recoveryCode: null, me: null, error: null, phase: 'login' });
  },

  acknowledgeRecovery: async () => {
    try {
      await get().client.ackRecoveryCode();
    } catch {
      /* non-fatal */
    }
    set({ recoveryCode: null });
  },

  wipeStart: async () => {
    const { ok, expiresAt } = await get().client.wipeStart();
    return { ok, expiresAt };
  },

  wipeCancel: async () => {
    await get().client.wipeCancel();
  },

  wipeConfirm: async (token: string) => {
    // Destroys all vault crypto server-side; no unlock/UMK involved (that's the point). After this
    // the account is un-enrolled — the caller reloads into the fresh /welcome enrolment.
    await get().client.wipeConfirm({ token, confirm: 'WIPE' });
    const { session, client } = get();
    if (session !== null) {
      wipeSession(session);
    }
    client.setSession(null);
    set({ session: null, recoveryCode: null, me: null });
  },

  dismissError: () => set({ error: null }),
}));

function armAutoLock(): void {
  if (lockTimer !== undefined) {
    clearTimeout(lockTimer);
  }
  lockTimer = setTimeout(() => {
    useSession.getState().lock();
  }, IDLE_MS);
}

/** Reset the idle timer on user activity (wired from the shell). */
export function bumpActivity(): void {
  if (useSession.getState().phase === 'unlocked') {
    armAutoLock();
  }
}
